import type {
  GstFiling,
  GstFilingInput,
  GstFilingListResponse,
  GstPeriodSummary,
  GstYearSummary,
  ClientInput,
  Client,
  ClientListQuery,
  ClientListResponse,
  ServiceInput,
  Service,
  ServiceListResponse,
  InvoiceInput,
  InvoicePresentationInput,
  Invoice,
  InvoiceListQuery,
  InvoiceListResponse,
  PaymentInput,
  Payment,
  Purchase,
  PurchaseInput,
  PurchaseListQuery,
  PurchaseListResponse,
  OrgSettings,
  OrgSettingsInput,
  AuthUser,
  SessionResponse,
  ChangePasswordRequest,
  DashboardStats,
  SendEmailRequest,
  StatementSendRequest,
  StatementSendResponse,
  StatementLog,
} from "@billing/shared";
import { Err, Ok, type Result } from "./result";
import { useAuthStore, getCsrf } from "./auth-store";

export const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "https://api-bill.dyuthitech.in";

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
const WRITE_METHODS = new Set<Method>(["POST", "PUT", "PATCH", "DELETE"]);

type RequestOpts = {
  method?: Method;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  isFormData?: boolean;
};

/** Low-level fetch: attaches the session cookie + CSRF header. */
function rawFetch(url: string, opts: RequestOpts): Promise<Response> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {};
  if (!opts.isFormData && opts.body !== undefined)
    headers["Content-Type"] = "application/json";
  // Double-submit CSRF: echo the token the server put in the readable cookie.
  if (WRITE_METHODS.has(method)) {
    const csrf = getCsrf();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  return fetch(url, {
    method,
    headers,
    // Send the HttpOnly session cookie with every request (incl. cross-origin).
    credentials: "include",
    body: opts.isFormData
      ? (opts.body as BodyInit)
      : opts.body !== undefined
        ? JSON.stringify(opts.body)
        : undefined,
    signal: opts.signal,
  });
}

/** Ask the server to rotate/refresh the cookie session once. */
async function tryRefresh(): Promise<boolean> {
  try {
    const r = await rawFetch(`${BASE_URL}/auth/refresh`, { method: "POST" });
    if (!r.ok) return false;
    const body = (await r.json()) as { data?: SessionResponse };
    if (body?.data?.user && body.data.csrf_token) {
      useAuthStore.getState().setSession(body.data.user, body.data.csrf_token);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<Result<T>> {
  const url = new URL(path, BASE_URL);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
    }
  }
  const target = url.toString();

  let response: Response;
  try {
    response = await rawFetch(target, opts);
    // On a 401, transparently refresh the cookie session once and retry — unless
    // this IS an auth endpoint (avoid loops).
    if (
      response.status === 401 &&
      !path.startsWith("/auth/") &&
      useAuthStore.getState().authed
    ) {
      if (await tryRefresh()) {
        response = await rawFetch(target, opts);
      }
    }
  } catch (e) {
    return Err({
      code: "INTERNAL_ERROR",
      message: e instanceof Error ? e.message : "Network error",
    });
  }

  if (response.status === 204) return Ok(undefined as T);

  let payload: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { error: { code: "INTERNAL_ERROR", message: text } };
    }
  }

  if (response.status === 401) {
    useAuthStore.getState().clear();
    if (typeof window !== "undefined" && window.location.pathname !== "/login") {
      window.location.assign("/login");
    }
  }

  if (!response.ok || !payload || typeof payload !== "object") {
    const err =
      payload && typeof payload === "object" && "error" in payload
        ? (payload as { error: { code: string; message: string; details?: unknown } }).error
        : { code: "INTERNAL_ERROR", message: response.statusText || "Request failed" };
    return Err({
      code: (err.code as never) ?? "INTERNAL_ERROR",
      message: err.message ?? "Request failed",
      details: err.details,
    });
  }
  if ("error" in (payload as object)) {
    const err = (payload as { error: { code: string; message: string; details?: unknown } }).error;
    return Err({
      code: (err.code as never) ?? "INTERNAL_ERROR",
      message: err.message,
      details: err.details,
    });
  }
  return Ok((payload as { data: T }).data);
}

/** Which sign-in the API runs: the Dyuthi SSO redirect or the app's own form. */
export type AuthMode = "keycloak" | "accounts";

/** Why the API refused a sign-in (the contract shared with the native apps). */
export type SignInError =
  | "invalid"
  | "mfa_required"
  | "setup"
  | "noemail"
  | "noaccess"
  | "throttled"
  | "unavailable";

export type SignInReply =
  | { ok: true; data: SessionResponse }
  | { ok: false; error: SignInError };

const SIGN_IN_ERRORS: ReadonlySet<string> = new Set<SignInError>([
  "invalid", "mfa_required", "setup", "noemail", "noaccess", "throttled", "unavailable",
]);

/**
 * POST /auth/login — the web sign-in form (AUTH_MODE=accounts). Answers
 * `{ ok, data }` / `{ ok:false, error }` like the apps' /auth/password, so it
 * is read here rather than through `request` (whose 401 handling would bounce
 * the user back to /login mid-attempt).
 */
async function signIn(body: { email: string; password: string; otp?: string }): Promise<SignInReply> {
  try {
    const response = await rawFetch(`${BASE_URL}/auth/login`, { method: "POST", body });
    const reply = (await response.json().catch(() => null)) as
      | { ok?: boolean; data?: SessionResponse; error?: unknown }
      | null;
    if (reply?.ok && reply.data?.user && reply.data.csrf_token) return { ok: true, data: reply.data };
    const code = typeof reply?.error === "string" && SIGN_IN_ERRORS.has(reply.error) ? reply.error : null;
    return { ok: false, error: (code as SignInError | null) ?? (response.status === 429 ? "throttled" : "unavailable") };
  } catch {
    return { ok: false, error: "unavailable" };
  }
}

export const api = {
  auth: {
    // Public: which sign-in to show.
    config: () => request<{ mode: AuthMode }>("/auth/config"),
    signIn,
    // Bootstrap the client session from the HttpOnly cookie. 401 if not signed in.
    session: () => request<SessionResponse>("/auth/session"),
    me: () => request<AuthUser>("/auth/me"),
    // Destroy the server session + cookies (and the IdP session); returns where to go next.
    logout: () =>
      request<{ end_session_url: string }>("/auth/logout", { method: "POST" }),
    changePassword: (body: ChangePasswordRequest) =>
      request<{ success: true }>("/auth/change-password", {
        method: "POST",
        body,
      }),
  },
  settings: {
    get: () => request<OrgSettings>("/settings"),
    update: (body: OrgSettingsInput) =>
      request<OrgSettings>("/settings", { method: "PUT", body }),
    uploadLogo: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return request<{ logo_r2_key: string }>("/settings/logo", {
        method: "POST",
        body: fd,
        isFormData: true,
      });
    },
  },
  clients: {
    list: (q?: ClientListQuery) =>
      request<ClientListResponse>("/clients", { query: q as never }),
    get: (id: string) => request<Client>(`/clients/${id}`),
    create: (body: ClientInput) =>
      request<Client>("/clients", { method: "POST", body }),
    update: (id: string, body: ClientInput) =>
      request<Client>(`/clients/${id}`, { method: "PUT", body }),
    remove: (id: string) =>
      request<{ success: true }>(`/clients/${id}`, { method: "DELETE" }),
  },
  services: {
    list: (q?: { q?: string }) =>
      request<ServiceListResponse>("/services", { query: q }),
    get: (id: string) => request<Service>(`/services/${id}`),
    create: (body: ServiceInput) =>
      request<Service>("/services", { method: "POST", body }),
    update: (id: string, body: ServiceInput) =>
      request<Service>(`/services/${id}`, { method: "PUT", body }),
    remove: (id: string) =>
      request<{ success: true }>(`/services/${id}`, { method: "DELETE" }),
  },
  invoices: {
    list: (q?: InvoiceListQuery) =>
      request<InvoiceListResponse>("/invoices", { query: q as never }),
    get: (id: string) => request<Invoice>(`/invoices/${id}`),
    create: (body: InvoiceInput) =>
      request<Invoice>("/invoices", { method: "POST", body }),
    update: (id: string, body: InvoiceInput) =>
      request<Invoice>(`/invoices/${id}`, { method: "PUT", body }),
    // HSN/SAC, unit and service period only — safe on an issued invoice.
    updatePresentation: (id: string, body: InvoicePresentationInput) =>
      request<Invoice>(`/invoices/${id}/presentation`, {
        method: "PATCH",
        body,
      }),
    remove: (id: string) =>
      request<{ success: true }>(`/invoices/${id}`, { method: "DELETE" }),
    finalize: (id: string) =>
      request<Invoice>(`/invoices/${id}/finalize`, { method: "POST" }),
    cancel: (id: string) =>
      request<Invoice>(`/invoices/${id}/cancel`, { method: "POST" }),
    email: (id: string, body: SendEmailRequest) =>
      request<{ success: true }>(`/invoices/${id}/email`, {
        method: "POST",
        body,
      }),
    uploadPdf: (id: string, pdf_base64: string) =>
      request<{ pdf_r2_key: string }>(`/invoices/${id}/pdf-uploaded`, {
        method: "POST",
        body: { pdf_base64 },
      }),
    getPdfUrl: (id: string) =>
      request<{ url: string }>(`/invoices/${id}/pdf-url`),
    listPayments: (id: string) =>
      request<Payment[]>(`/invoices/${id}/payments`),
  },
  payments: {
    create: (body: PaymentInput) =>
      request<Payment>("/payments", { method: "POST", body }),
    remove: (id: string) =>
      request<{ success: true }>(`/payments/${id}`, { method: "DELETE" }),
  },
  purchases: {
    list: (q?: PurchaseListQuery) =>
      request<PurchaseListResponse>("/purchases", { query: q as never }),
    get: (id: string) => request<Purchase>(`/purchases/${id}`),
    create: (body: PurchaseInput) =>
      request<Purchase>("/purchases", { method: "POST", body }),
    update: (id: string, body: PurchaseInput) =>
      request<Purchase>(`/purchases/${id}`, { method: "PUT", body }),
    remove: (id: string) =>
      request<{ success: true }>(`/purchases/${id}`, { method: "DELETE" }),
    uploadBill: (id: string, file_base64: string, content_type: string) =>
      request<{ bill_r2_key: string }>(`/purchases/${id}/bill-uploaded`, {
        method: "POST",
        body: { file_base64, content_type },
      }),
  },
  statements: {
    list: () => request<{ items: StatementLog[] }>("/statements"),
    preview: (period?: string) =>
      request<{ period: string; totals: StatementSendResponse["totals"] }>(
        "/statements/preview",
        { query: { period } },
      ),
    send: (body: StatementSendRequest) =>
      request<StatementSendResponse>("/statements/send", {
        method: "POST",
        body,
      }),
  },
  gstFilings: {
    list: () => request<GstFilingListResponse>("/gst-filings"),
    /** Recomputed from current invoices/purchases on every call. */
    period: (period?: string) =>
      request<GstPeriodSummary>("/gst-filings/period", { query: { period } }),
    /** Every month of an Indian financial year (April → March) so far. */
    year: (fy?: number) =>
      request<GstYearSummary>("/gst-filings/year", {
        query: { fy: fy === undefined ? undefined : String(fy) },
      }),
    record: (body: GstFilingInput) =>
      request<GstFiling>("/gst-filings", { method: "POST", body }),
    remove: (id: string) =>
      request<{ success: true }>(`/gst-filings/${id}`, { method: "DELETE" }),
  },
  dashboard: {
    stats: () => request<DashboardStats>("/dashboard"),
  },
};
